import { DQLResult } from '@portal/dql-editor'
import dayjs from 'dayjs'
import getProperty from 'lodash/get'
import semver from 'semver'

import { DeviceLogLevel } from './types'

export const isDeviceLogLevel = (value: unknown): value is DeviceLogLevel =>
  typeof value === 'string' &&
  ['INFO', 'DEBUG', 'TRACE', 'WARN', 'ERROR'].includes(value)

export const numberField = (
  result: DQLResult,
  path: string,
  defaultIfMissing = 0,
): number => {
  const value = getProperty(result, path)
  return typeof value === 'number' ? value : defaultIfMissing
}

export const booleanField = (result: DQLResult, path: string): boolean => {
  const value = getProperty(result, path)
  return typeof value === 'boolean' ? value : Boolean(value)
}

export const stringField = (result: DQLResult, path: string): string => {
  const value = getProperty(result, path)
  return typeof value === 'string' ? value : ''
}

export const objectField = (
  result: DQLResult,
  path: string,
): Record<string, unknown> => {
  const value = getProperty(result, path)
  return typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

export const dayJsValue = (date: string) => {
  const value = dayjs(date)
  return value.isValid() ? value : undefined
}

export const getResponseAttachmentId = (
  result: DQLResult,
  responsesPath: string,
  timestamp: string,
): string | undefined => {
  const value = objectField(result, `${responsesPath}["${timestamp}"]`)?.id
  return typeof value === 'string' ? value : undefined
}

export const logAttachmentId = (
  result: DQLResult,
  timestamp: string,
  logsResponsesPath: string,
): string | undefined => {
  return getResponseAttachmentId(result, logsResponsesPath, timestamp)
}

export const getResponseStatus = (
  result: DQLResult,
  responseStatusPath: string,
): { success: boolean; message: string } | null => {
  const status = getProperty(result, responseStatusPath)
  if (
    status &&
    typeof status === 'object' &&
    'success' in status &&
    typeof status.success === 'boolean'
  ) {
    return {
      success: status.success,
      message:
        'message' in status && typeof status.message === 'string'
          ? status.message
          : '',
    }
  }
  return null
}

/**
 * Parses a SDK version string and returns a semver-compatible version string.
 * Handles versions with or without leading 'v' (e.g., "v4.12.1" or "4.12.1").
 * @param version - The version string to parse
 * @returns A semver-compatible version string (e.g., '4.12.1'), or undefined if invalid
 */
export const parseSdkVersion = (version: string): string | undefined => {
  if (!version || typeof version !== 'string') {
    return undefined
  }

  const coerced = semver.coerce(version)
  return coerced ? coerced.version : undefined
}
